import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Switch } from '../../ui/switch';
import { Label } from '../../ui/label';
import { Wallet, Loader2, RefreshCw, Search } from 'lucide-react';
import { disputesAPI } from '../../../services/api';
import { toast } from 'sonner';

// O MP avisa por webhook, mas a vigilância pelos pagamentos é a rede de segurança:
// abre o que apareceu e encerra o que já foi decidido.
export const MpWatchPanel = ({ onChanged }) => {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      setCfg((await disputesAPI.mpWatch()).data);
    } catch (e) {
      /* sem permissão ou fora do ar: o card simplesmente não aparece */
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const alternar = async (v) => {
    setBusy('save');
    try {
      setCfg((await disputesAPI.mpWatchUpdate(v)).data);
      toast.success(v ? 'Vigilância do Mercado Pago ligada' : 'Vigilância desligada');
    } catch (e) {
      toast.error('Falha ao salvar');
    } finally { setBusy(''); }
  };

  const rodar = async (qual) => {
    setBusy(qual);
    try {
      const r = qual === 'run' ? await disputesAPI.mpWatchRun() : await disputesAPI.mpSearch(365);
      const d = r.data || {};
      if (d.ok === false) {
        toast.error(d.message || 'Consulta não concluída', { duration: 12000 });
      } else {
        toast.success(`${d.found || 0} em disputa · ${d.new || 0} nova(s)`
          + (d.closed ? ` · ${d.closed} encerrada(s)` : '')
          + (d.message ? `\n${d.message}` : ''), { duration: 16000 });
      }
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha na consulta');
    } finally { setBusy(''); }
  };

  if (!cfg) return null;
  const ultimo = cfg.last_result || {};

  return (
    <Card data-testid="mp-watch-panel" className="border-sky-200 bg-sky-50/40">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Wallet className="h-5 w-5 text-sky-700" /> Mercado Pago · Contestações
          {!cfg.configured && (
            <Badge className="bg-amber-500 text-white">credenciais ausentes</Badge>
          )}
        </CardTitle>
        <CardDescription>
          Funciona sozinho: além do aviso por webhook, a cada 30 minutos o sistema olha os
          pagamentos da conta. Pagamento em mediação entra como <b>Aberta</b> e bloqueia o
          cliente; virou chargeback, entra como <b>Perdemos</b>; saiu da mediação sem estorno,
          encerra como <b>Ganhamos</b> e libera o cliente.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-2">
            <Switch id="mp-watch" checked={cfg.enabled} disabled={busy === 'save'}
                    data-testid="mp-watch-enabled" onCheckedChange={alternar} />
            <Label htmlFor="mp-watch" className="text-sm">Vigilância automática (30 min)</Label>
          </div>
          <Button size="sm" variant="outline" onClick={() => rodar('run')}
                  disabled={busy === 'run'} data-testid="mp-watch-run">
            {busy === 'run' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              : <RefreshCw className="h-4 w-4 mr-1" />}
            Verificar agora
          </Button>
          <Button size="sm" variant="ghost" onClick={() => rodar('hist')}
                  disabled={busy === 'hist'} data-testid="mp-search-history">
            {busy === 'hist' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              : <Search className="h-4 w-4 mr-1" />}
            Buscar 12 meses
          </Button>
        </div>
        {cfg.last_run && (
          <p className="text-xs text-gray-600" data-testid="mp-watch-last">
            Última verificação: {new Date(cfg.last_run).toLocaleString('pt-BR')}
            {` · ${ultimo.found || 0} em disputa · ${ultimo.new || 0} nova(s)`}
            {ultimo.closed ? ` · ${ultimo.closed} encerrada(s)` : ''}
            {(ultimo.errors || []).length ? ` · ${ultimo.errors.length} erro(s)` : ''}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default MpWatchPanel;
